import { createCloudRunAxios } from './axios';
import { rethrowWithDetail } from './remiApi';
import { pollUntilSettled } from './uploadStatusPolling';

const remiApi = createCloudRunAxios('remi-api');

/**
 * Bulk assign contacts to a team member
 * @param organizationId The organization ID
 * @param name The name of the team member
 * @param phoneNumber The phone number of the team member
 * @param email The email of the team member (optional)
 * @param tag The tag to filter contacts by (optional)
 * @param reAssign Whether to reassign contacts that are already assigned
 * @returns The response from the API with details about updated and skipped contacts
 */
export const bulkAssignContacts = async (
  organizationId: string,
  name: string,
  phoneNumber: string,
  email: string,
  tag?: string,
  reAssign: boolean = false
) => {
  try {
    // Prepare the request payload
    const payload: {
      organizationId: string;
      name: string;
      phoneNumber: string;
      reAssign: boolean;
      email: string;
      tag?: string;
    } = {
      organizationId,
      name,
      phoneNumber,
      reAssign,
      email,
    };

    // Add tag if provided
    if (tag) {
      payload.tag = tag;
    }

    const response = await remiApi.post('/api/v1/bulk-claim-leads', payload);

    // The API response should include:
    // {
    //   "success": true,
    //   "message": "Successfully processed bulk claim leads request",
    //   "updated": 10,  // Number of contacts updated
    //   "skipped": 2    // Number of contacts skipped (due to reAssign=false)
    // }
    return response.data;
  } catch (error) {
    console.error('Error bulk assigning contacts:', error);
    throw error;
  }
};

export interface BulkTagsResponse {
  success: boolean;
  mode: 'preview' | 'execute';
  operation: 'add' | 'remove' | 'replace';
  tags: string[];
  matched_contacts: number;
  contact_ids: string[];
  truncated: boolean;
  updated_contacts: number;
  preview?: {
    matched_contacts: number;
    would_update_contacts: number;
    unchanged_contacts: number;
    sample_changes: Array<{
      contact_id: string;
      name: string;
      added_tags: string[];
      removed_tags: string[];
      result_tags: string[];
    }>;
    sample_limit: number;
  };
  idempotency_key?: string;
}

/**
 * Add tags to a selection of contacts in one server-side call. The endpoint
 * merges against authoritative current tags so existing Speculo / FUB tags
 * are preserved — replaces the previous per-contact PUT-loop that dropped
 * unrelated tags (SPEC-1858).
 *
 * `selector.limit` caps how many contacts a selector resolves; default 1000
 * server-side. Pass enough headroom for the actual selection and inspect
 * `truncated` in the response.
 */
export const bulkAddTags = async (
  organizationId: string,
  contactIds: string[],
  tags: string[],
): Promise<BulkTagsResponse> => {
  const response = await remiApi.post<BulkTagsResponse>('/api/v1/contacts/tags/bulk', {
    organization_id: organizationId,
    mode: 'execute',
    operation: 'add',
    selector: {
      contact_ids: contactIds,
      limit: Math.max(contactIds.length, 1000),
    },
    tags,
  });
  return response.data;
};

/**
 * Remove a specific tag from all contacts that have it
 * @param organizationId The organization ID
 * @param tag The tag to remove from contacts
 * @returns The response from the API with details about updated contacts
 */
export const removeTagFromContacts = async (organizationId: string, tag: string) => {
  try {
    const response = await remiApi.post('/api/v1/contacts/remove-tag', {
      organization_id: organizationId,
      tag,
    });
    return response.data;
  } catch (error) {
    console.error('Error removing tag from contacts:', error);
    throw error;
  }
};

/**
 * Delete all contacts with a specific tag
 * @param organizationId The organization ID
 * @param tag The tag to delete contacts by
 * @returns The response from the API with details about deleted contacts, flow executions, and cloud tasks
 */
export const deleteContactsByTag = async (organizationId: string, tag: string) => {
  try {
    const response = await remiApi.post('/api/v1/contacts/delete-by-tag', {
      organization_id: organizationId,
      tag,
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting contacts by tag:', error);
    throw error;
  }
};

/**
 * Upload contacts in bulk from a file
 * @param file The file to upload (CSV, XLSX, or XLS)
 * @param organizationId The organization ID
 * @param optIn Whether to opt in the contacts
 * @param tag The tag to apply to the contacts
 * @param onProgress Optional callback for tracking upload progress
 * @returns The response from the API
 */
export interface UploadStatusResponse {
  status: 'queued' | 'processing' | 'completed' | 'failed';
  request_id: string;
  processed?: number;
  created?: number;
  updated?: number;
  skipped?: number;
  total?: number;
  /** 0–100 while the backend is processing rows. */
  progress?: number | null;
  opt_in?: boolean;
  upload_tag?: string;
  message?: string;
  errors?: Array<{ contact: Record<string, string>; error: string }>;
  uploadBatchId?: string;
  uploadDateTime?: string;
  success?: boolean;
}

/**
 * Upload contacts CSV/Excel to the backend.
 * Returns the initial response which may include a request_id for async processing.
 */
export const bulkUploadContacts = async (
  file: File,
  organizationId: string,
  optIn: boolean,
  tag: string,
  onProgress?: (progress: number, phase: 'upload' | 'processing') => void
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('org_id', organizationId);
  formData.append('opt_in', optIn.toString());
  formData.append('upload_tag', tag);

  const response = await remiApi
    .post('/api/v1/contact-uploader', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress?.(pct, 'upload');
        }
      },
    })
    .catch(rethrowWithDetail);

  // Processing progress restarts at 0 and is driven by the status poll.
  onProgress?.(0, 'processing');
  return response.data;
};

/**
 * Poll upload status until completed or failed.
 */
export const getUploadStatus = async (
  requestId: string,
  organizationId: string
): Promise<UploadStatusResponse> => {
  const response = await remiApi.get<UploadStatusResponse>(
    `/api/v1/contact-uploader/status/${requestId}`,
    { params: { org_id: organizationId } }
  );
  return response.data;
};

/**
 * Poll upload status until completed or failed. Throws
 * `UploadStillProcessingError` if the upload stops making progress; it may
 * still finish on the backend.
 */
export const pollUploadStatus = (
  requestId: string,
  organizationId: string,
  onStatus?: (status: UploadStatusResponse) => void
): Promise<UploadStatusResponse> =>
  pollUntilSettled(() => getUploadStatus(requestId, organizationId), onStatus);

/**
 * Reason string the user enters when pausing a contact. The server trims
 * the value and prepends "manual: " before persisting; the display layer
 * strips that prefix back off via `stripManualPausePrefix`.
 */
const PAUSE_REASON_MAX = 247;

/**
 * Pause Remi's outreach on a single contact. Server stamps `ai_paused`,
 * `ai_paused_at`, and `ai_paused_reason = "manual: <reason>"` in one
 * transaction; the call is idempotent. Throws on the FastAPI error envelope.
 */
export const pauseContact = async (
  contactId: string,
  organizationId: string,
  reason: string,
): Promise<unknown> => {
  const trimmed = reason.trim();
  if (!trimmed) throw new Error('A reason is required to pause this contact.');
  if (trimmed.length > PAUSE_REASON_MAX) {
    throw new Error(`Reason must be ${PAUSE_REASON_MAX} characters or fewer.`);
  }
  const response = await remiApi.post(
    `/api/v1/contacts/${contactId}/pause`,
    { reason: trimmed },
    { params: { organization_id: organizationId } },
  );
  return response.data;
};

/** Resume Remi's outreach on a previously-paused contact. Idempotent. */
export const unpauseContact = async (
  contactId: string,
  organizationId: string,
): Promise<unknown> => {
  const response = await remiApi.post(
    `/api/v1/contacts/${contactId}/unpause`,
    {},
    { params: { organization_id: organizationId } },
  );
  return response.data;
};

export { stripManualPausePrefix, formatPauseReason } from './pauseReason';
